<script setup lang="ts">
import {cx} from 'class-variance-authority'
import {lexicalToPlaintext} from '~/utils/lexical'
import {resolveRelations} from '~/utils/relations'

import ReviewsSmall from '~/components/workshop/reviews-small.vue'
import {useWorkshop} from '~/composables/cms/workshop'

import CheckGreen from '~/assets/icons/check-green.svg?component'

import Menu from '~/components/workshop/menu.vue'

import WorkshopDescription from '~/components/workshop/workshop-description.vue'
import CardsSwiper from '~/components/workshop/cards-swiper.vue'
import Prices from '~/components/workshop/prices.vue'
import OurProjects from '~/components/workshop/our-projects.vue'
import WorkshopTeam from '~/components/workshop/workshop-team.vue'
import BlogSection from '~/components/workshop/blog-section.vue'

import SingleTestimonial from '~/components/managed-projects/single-testimonial.vue'
import OtherServices from '~/components/workshop/other-services.vue'

const {
  public: {baseUrl},
} = useRuntimeConfig()

const {fetch} = useWorkshop()
const lp = useLocalePath()
const {data: initialData} = await useAsyncData('workshop-page', () => fetch())
const {data: workshop} = useLivePreview({initialData: initialData.value, depth: 4})

if (!workshop.value) {
  throw createError({status: 503, message: 'Could not load the page.'})
}

useSchemaOrg([
  {
    '@type': 'FAQPage',
    mainEntity: computed(
      () =>
        resolveRelations(workshop.value?.faq?.questions).map(item => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: lexicalToPlaintext(item.body),
          },
        })) || []
    ),
  },
])

const pixelGridLg = [
  '  111111111111111111111111111111111111111111111   ',
  ' 1111111111111111     111111211111111111111111    ',
  '  112221111111122111111111221211111111111111111   ',
  '       211111111111111111111111111111    2111111  ',
  '       211111111111111111111111111111    2111111  ',
  '                                    1    2111111  ',
  '       2111111                     11    2111111  ',
  '1111                     111111122111 221111111   ',
  '1111111111                  111111122111 221111111',
  '1111111111                                        ',
  '                            111111122111 221111111',
  '                            111111122111 221111111',
  '                                  122111 221111111',
  '                                      11 221111111',
].join('\n')

const seo = workshop.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/product-discovery-workshop`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const currentTab = ref<string>('Startup')

function onTabChange(option: string) {
  currentTab.value = option
}

const gradient = 'text-transparent bg-clip-text text-transparent leading-tight bg-gradient-to-b'
</script>

<template>
  <main v-if="workshop" class="flex w-full flex-col overflow-hidden">
    <Container
      class="screen-content relative flex flex-col items-center space-y-6 pb-16 pt-32 md:pb-16 lg:pb-16 lg:pt-48"
    >
      <div class="mt-16 flex w-full flex-1 items-center justify-between">
        <div>
          <ReviewsSmall class="text-center lg:text-left" />

          <Heading size="display" as="h1" class="w-full xs:mb-8 xs:text-center lg:mb-4 lg:text-left">
            <span :class="cx(gradient, 'from-gray-950 to-gray-600')">{{ $t('workshop.title.top') }}</span>
            <br />
            <span class="pr-1" :class="cx(gradient, 'from-main-700 to-main-500')">
              {{ $t('workshop.title.bottom') }}
            </span>
          </Heading>

          <Paragraph
            v-if="workshop.hero?.body"
            class="mb-8 text-gray-600 xs:px-12 xs:text-center lg:w-[85%] lg:px-0 lg:text-left"
            size="medium"
          >
            {{ workshop.hero.body }}
          </Paragraph>

          <div class="flex flex-col sm:justify-center lg:justify-start">
            <div class="flex flex-col space-x-0 xs:space-y-4 md:flex-row md:space-x-4 lg:space-y-0">
              <Action
                v-if="workshop.hero?.action?.href"
                class="lg:text-md flex w-full items-center justify-center py-3 text-sm font-normal md:w-28 lg:w-44"
                as="link"
                :intent="workshop.hero.action.intent"
                :to="workshop.hero.action.href"
              >
                {{ workshop.hero.action.label }}
              </Action>

              <Action
                class="lg:text-md flex w-full items-center justify-center py-3 text-sm font-normal md:w-28 lg:w-32"
                intent="normal"
                as="button"
                @click="() => $scrollTo('prices')"
              >
                {{ $t('workshop.hero_button') }}
              </Action>
            </div>

            <Paragraph size="small" class="invisible py-8 pt-4 text-gray-600 xs:text-center sm:visible lg:text-left">
              Just
              <span class="font-bold text-gray-800">{{ (workshop.hero?.spots ?? '') + ' spots' + ' ' }}</span>
              left this month
            </Paragraph>
          </div>
        </div>

        <Menu class="hidden md:block" @update-tab="onTabChange" :title="workshop.card?.title ?? undefined">
          <li
            v-for="option in currentTab === 'Startup' ? workshop.card?.startupOptions : workshop.card?.options"
            class="text-md mb-4 flex list-none items-center gap-2 space-y-2 rounded-md border border-gray-300 p-3 text-gray-600"
          >
            <CheckGreen class="h-6 w-6" />
            {{ typeof option === 'string' ? option : option.title }}
          </li>
        </Menu>
      </div>

      <Partners
        v-if="workshop.testimonials?.partners"
        :partners="workshop.testimonials.partners"
        :title="false"
        class="mt-4 px-8 pb-0 lg:px-0"
      />

      <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
        <PixelGrid class="h-full" :pattern="pixelGridLg" />
      </div>
    </Container>

    <Container v-if="workshop.description" class="mx-auto flex flex-col items-center">
      <WorkshopDescription :data="workshop.description" />
    </Container>

    <CardsSwiper v-if="workshop.swipeCard" :data="workshop.swipeCard" />

    <div v-if="workshop.pricing" id="prices">
      <Prices :data="workshop.pricing" />
    </div>

    <OurProjects v-if="workshop.projects" :data="workshop.projects" />

    <WorkshopTeam v-if="workshop.team" :data="workshop.team" />

    <SingleTestimonial v-if="workshop.succession" class="border-b-0 py-14 lg:py-24" :data="workshop.succession" />

    <Container v-if="workshop.testimonials" class="screen-content flex flex-col items-center px-2">
      <Testimonials class="w-full" :testimonials="workshop.testimonials.testimonials ?? []" />
    </Container>

    <Container
      v-if="workshop.faq"
      class="screen-content flex flex-col items-center pb-16 pt-32 text-center md:pb-16 lg:pb-16 lg:pt-48"
    >
      <Heading size="caption" as="h2">{{ workshop.faq.headline }}</Heading>
      <Heading as="h3" size="large" class="w-full lg:w-1/2">{{ workshop.faq.title }}</Heading>
      <Paragraph class="mt-2 w-full pb-6 lg:w-1/2">{{ workshop.faq.body }}</Paragraph>

      <Accordion
        v-if="workshop.faq.questions"
        class="mt-8 pb-16 text-sm xs:px-12 xl:px-72"
        :items="workshop.faq.questions"
      />
    </Container>

    <BlogSection v-if="workshop.blog" :data="workshop.blog" />

    <OtherServices v-if="workshop.otherServices" :data="workshop.otherServices" />

    <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
  </main>
</template>
